import { FormControl, FormHelperText, Grid } from "@mui/material";
import Field from "components/Field";
import SelectEntityAsync from "components/SelectEntityAsync";
import useGlobalContext from "hooks/globalData";
import {
  attributeTypeConfigs,
  getMetaValueComponent,
} from "pages/AttributesPage/CustomAttributeComponents";
import { useIntl } from "react-intl";
import { useQueryClient } from "react-query";
import {
  AttributeFilter,
  AttributeMetaValue,
  ListRequestOfAttributeFilter,
  ListResponseOfAttribute,
  OrganizationAttributeType,
  SkuAttributeByType,
} from "services/KnowfullyAdminClient";

export const SkuAttributeFormComponent = (
  skuAttributes: SkuAttributeByType[],
  values: { value?: string; displayText?: string; attributeMetaValues?: AttributeMetaValue[] },
  organizationAttributeType: OrganizationAttributeType,
  editingAttributeId?: string
) => {
  const queryClient = useQueryClient();
  const { formatMessage: t } = useIntl();

  const { attributeService } = useGlobalContext();

  const fetchAttributes = async (searchTerm: string) => {
    let result = new ListResponseOfAttribute();
    if (organizationAttributeType?.id) {
      let excludedIds = skuAttributes
        .find(
          (x) => x.organizationAttributeTypeId === organizationAttributeType.id
        )
        ?.attributes?.map((x) => x.attributeId)
        ?.filter((id) => id !== editingAttributeId);

      result = await queryClient.fetchQuery<ListResponseOfAttribute>(
        ["attributes", organizationAttributeType.id, searchTerm, excludedIds],
        () =>
          attributeService.getAttributes(
            new ListRequestOfAttributeFilter({
              pageNumber: 0,
              pageSize: 20,
              parameters: AttributeFilter.fromJS({
                searchText: searchTerm === "" ? undefined : searchTerm,
                organizationAttributeTypeId: organizationAttributeType?.id!,
                excludedIds,
              }),
              sortField: "displayText",
            })
          )
      );
    }

    return { result, forSearchTerm: searchTerm };
  };

  const editingInitialValue = editingAttributeId
    ? { data: { id: editingAttributeId, value: values.value } }
    : undefined;

  const renderSelectAttribute = () => {
    return (
      <Field name="attributeId">
        {({ input, meta }) => (
          <Grid item xs={12}>
            <FormControl fullWidth error={meta.error && meta.submitFailed}>
              <SelectEntityAsync
                fetchData={fetchAttributes}
                value={input.value}
                onChange={input.onChange}
                initialValue={editingInitialValue}
                label={
                  organizationAttributeType?.attributeType?.name || "Attribute"
                }
                labelField={"value"}
                failMessage={t({ id: "fetchError" })}
              />
              {meta.error && meta.submitFailed && (
                <FormHelperText>{meta.error}</FormHelperText>
              )}
            </FormControl>
          </Grid>
        )}
      </Field>
    );
  };

  const attributeConfig = attributeTypeConfigs.find(
    (c) =>
      c.codeConstant ===
      organizationAttributeType?.attributeType?.codeConstant?.toLowerCase()
  );

  const hasInlineInsert =
    attributeConfig &&
    organizationAttributeType?.attributeType?.allowInlineInserts;

  if (!hasInlineInsert) return renderSelectAttribute();

  return (
    <>
      {attributeConfig.metaValues.map((metaValueName, index) => {
        const config = getMetaValueComponent(metaValueName);
        if (!config) return null;
        const { FormField } = config;
        return (
          <FormField
            key={metaValueName}
            metaValueName={metaValueName}
            index={index}
            attributeMetaValues={values.attributeMetaValues}
          />
        );
      })}
    </>
  );
};
